import React from 'react';
import { Box, Divider, Typography, List, Alert, Button } from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import type { OrganizationWithRole } from '../api.ts';
import OrganizationListItem from './OrganizationListItem.tsx';

type Props = {
  organizations: OrganizationWithRole[];
  loading?: boolean;
  currentOrganizationId?: number | null;
  onSwitchOrg: (org: OrganizationWithRole) => void;
  onManageOrg?: (org: OrganizationWithRole) => void;
  onCreateClick?: () => void;
};

const OrganizationsList: React.FC<Props> = ({
  organizations,
  loading,
  currentOrganizationId,
  onSwitchOrg,
  onManageOrg,
  onCreateClick,
}) => {
  const hasNoOrganization = organizations.length === 0 && !loading;

  return (
    <>
      {hasNoOrganization && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You don't have any organizations yet. Create one to start managing risks.
        </Alert>
      )}

      {/* Current organization */}
      {!hasNoOrganization && currentOrganizationId != null && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Current Organization:
          </Typography>
          <Typography variant="h6">
            {organizations.find((o) => o.id === currentOrganizationId)?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Role: {organizations.find((o) => o.id === currentOrganizationId)?.role}
          </Typography>
        </Box>
      )}

      {organizations.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            All Organizations:
          </Typography>
          <List dense>
            {organizations.map((org) => (
              <OrganizationListItem
                key={org.id}
                organization={org}
                isActive={currentOrganizationId === org.id}
                canManage={org.role === 'admin' || org.role === 'owner'}
                onSwitch={onSwitchOrg}
                onManage={onManageOrg}
              />
            ))}
          </List>
        </>
      )}

      <Button
        variant="contained"
        color="primary"
        startIcon={<BusinessCenterIcon />}
        onClick={onCreateClick}
        sx={{ mt: 2 }}
      >
        Create New Organization
      </Button>
    </>
  );
};

export default OrganizationsList;
